/**
 * `encodeExtra` (W1-08 formats-legacy-respack; contract formats/legacy/types.ts): IrExtraJson -> extra.json bytes
 * that Phira loads (formats-and-packaging.md P§5.1, DESIGN.md §7.3):
 *
 *  - root keys `bpm` (the chart's BPMList as {time, bpm}, always written, BPMs as f64 exactly like the chart's
 *    RPE BPMList so the re-import keeps every time as written), `effects` and `videos` (omitted when empty: no
 *    empty arrays), then the passthrough root keys (never overriding a known key);
 *  - every beat triple as three JSON integers (EncodeError when c = 0);
 *  - variables: null, a float, a vec2 of floats, or a colour of four INTEGERS 0..255 (a float vec4 would match no
 *    shape in Phira and fail the file), or a NON-EMPTY keyframe list with the required integer `easingType`;
 *    keyframes never carry bezier fields (extra.json has none);
 *  - effects: start, end, shader, vars (omitted when empty), `global` only when true, then passthrough keys;
 *    videos: path, time, scale, alpha / dim when present, attach, then passthrough keys;
 *  - UTF-8 without BOM, first byte '{' (the JSON writer), RPE float style (`1.0`).
 */

import type { IrEffect, IrExtraJson, IrExtraKeyframe, IrVarValue, IrVideo } from '../../core/ir/types';
import type { Triple } from '../../core/time/types';
import type { Passthrough, RGBA8, Vec2 } from '../../core/util/types';
import { createJsonWriter } from '../json/writer';
import { EncodeError, type JsonWriter } from '../json/types';
import type { EncodeExtraFn } from '../legacy/types';

type Fixed = number | Vec2 | RGBA8;

function writeTriple(w: JsonWriter, t: Triple, path: string): void {
  if (t[2] === 0) throw new EncodeError(path, 'beat triple with denominator 0');
  w.beginArray();
  w.int(t[0], 'i32');
  w.int(t[1], 'u32');
  w.int(t[2], 'u32');
  w.endArray();
}

function writeFixed(w: JsonWriter, v: Fixed, path: string): void {
  if (typeof v === 'number') {
    w.f32(v);
    return;
  }
  const comps: readonly number[] = v;
  if (comps.length !== 2 && comps.length !== 4) {
    throw new EncodeError(path, `a variable value has ${String(comps.length)} components`);
  }
  w.beginArray();
  // A colour must be four integers 0..255 (Phira's u8 shape); a vec2 is two floats.
  if (comps.length === 2) for (const c of comps) w.f32(c);
  else for (const c of comps) w.int(c, 'u8');
  w.endArray();
}

function writePassthrough(w: JsonWriter, extra: Passthrough, known: readonly string[]): void {
  const seen = new Set<string>();
  for (const [k, v] of extra) {
    if (known.includes(k) || seen.has(k)) continue;
    seen.add(k);
    w.key(k);
    w.json(v);
  }
}

function writeKeyframe(w: JsonWriter, kf: IrExtraKeyframe<Fixed>, path: string): void {
  w.beginObject();
  w.key('startTime');
  writeTriple(w, kf.startTime, `${path}.startTime`);
  w.key('endTime');
  writeTriple(w, kf.endTime, `${path}.endTime`);
  w.key('start');
  writeFixed(w, kf.start, `${path}.start`);
  w.key('end');
  writeFixed(w, kf.end, `${path}.end`);
  w.key('easingType');
  w.int(kf.easingType, 'i32');
  if (kf.easingLeft !== undefined) {
    w.key('easingLeft');
    w.f32(kf.easingLeft);
  }
  if (kf.easingRight !== undefined) {
    w.key('easingRight');
    w.f32(kf.easingRight);
  }
  w.endObject();
}

/** The keyframe list of a variable, or null for a fixed value / null. */
function keyframesOf(v: IrVarValue): readonly IrExtraKeyframe<Fixed>[] | null {
  if (!Array.isArray(v)) return null;
  const list = v as readonly unknown[];
  return list.length === 0 || typeof list[0] === 'object' ? (list as readonly IrExtraKeyframe<Fixed>[]) : null;
}

function writeVar(w: JsonWriter, v: IrVarValue, path: string): void {
  if (v === null) {
    w.null();
    return;
  }
  const kfs = keyframesOf(v);
  if (kfs !== null) {
    if (kfs.length === 0) throw new EncodeError(path, 'empty keyframe list (Phira panics on it)');
    w.beginArray();
    kfs.forEach((kf, i) => {
      writeKeyframe(w, kf, `${path}[${String(i)}]`);
    });
    w.endArray();
    return;
  }
  writeFixed(w, v as Fixed, path);
}

const EFFECT_KEYS = ['start', 'end', 'shader', 'vars', 'global'];
const VIDEO_KEYS = ['path', 'time', 'scale', 'alpha', 'dim', 'attach'];
const ROOT_KEYS = ['bpm', 'effects', 'videos'];

function writeEffect(w: JsonWriter, e: IrEffect, path: string): void {
  w.beginObject();
  w.key('start');
  writeTriple(w, e.start, `${path}.start`);
  w.key('end');
  writeTriple(w, e.end, `${path}.end`);
  w.key('shader');
  w.string(e.shader);
  if (e.vars.length > 0) {
    w.key('vars');
    w.beginObject();
    const seen = new Set<string>();
    for (const [name, value] of e.vars) {
      if (seen.has(name)) throw new EncodeError(`${path}.vars`, `variable "${name}" written twice`);
      seen.add(name);
      w.key(name);
      writeVar(w, value, `${path}.vars.${name}`);
    }
    w.endObject();
  }
  if (e.global) {
    w.key('global');
    w.bool(true);
  }
  writePassthrough(w, e.extra, EFFECT_KEYS);
  w.endObject();
}

function writeVideo(w: JsonWriter, v: IrVideo, path: string): void {
  w.beginObject();
  w.key('path');
  w.string(v.path);
  w.key('time');
  writeTriple(w, v.time, `${path}.time`);
  w.key('scale');
  w.string(v.scale);
  for (const which of ['alpha', 'dim'] as const) {
    const value = v[which];
    if (value === undefined) continue;
    w.key(which);
    writeVar(w, value, `${path}.${which}`);
  }
  if (v.attach !== undefined) {
    w.key('attach');
    w.beginObject();
    w.key('line');
    w.int(v.attach.line, 'u32');
    w.endObject();
  }
  writePassthrough(w, v.extra, VIDEO_KEYS);
  w.endObject();
}

export const encodeExtra: EncodeExtraFn = (extra: IrExtraJson, pretty: boolean): Uint8Array => {
  const w = createJsonWriter({ pretty, floatStyle: 'rpe' });
  w.beginObject();
  w.key('bpm');
  w.beginArray();
  extra.bpm.forEach((p, i) => {
    const path = `bpm[${String(i)}]`;
    // A BPM <= 0 gives Phira infinite or backwards times (the RPE encoder refuses it too).
    if (!(p.bpm > 0)) throw new EncodeError(`${path}.bpm`, 'BPM must be positive');
    w.beginObject();
    w.key('time');
    writeTriple(w, p.time, `${path}.time`);
    w.key('bpm');
    // f64, like the chart's own BPMList: decodeExtra keeps the times as written only when the file's BPM list
    // equals the chart's in f64, so an f32 rounding (e.g. a tapped 120.00000000000001) would re-convert every time.
    w.f64(p.bpm);
    w.endObject();
  });
  w.endArray();
  if (extra.effects.length > 0) {
    w.key('effects');
    w.beginArray();
    extra.effects.forEach((e, i) => {
      writeEffect(w, e, `effects[${String(i)}]`);
    });
    w.endArray();
  }
  if (extra.videos.length > 0) {
    w.key('videos');
    w.beginArray();
    extra.videos.forEach((v, i) => {
      writeVideo(w, v, `videos[${String(i)}]`);
    });
    w.endArray();
  }
  writePassthrough(w, extra.rootExtra, ROOT_KEYS);
  w.endObject();
  return w.finish();
};
